import {AfterViewInit, Component, ElementRef, EventEmitter, NgModule, OnInit, Output, ViewChild, inject} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {TicketService} from "../../../core/services/services/ticket.service";
import {AlertEnum} from "../../../core/models/Enums/AlertEnum";
import {MessageService} from "../../../core/services/services/message.service";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import {AuthenticationService} from "../../../core/services/services/authentication.service";
import {ApplicationUser} from "../../../core/models/ApplicationUser";
import {CustomizingService} from "../../../core/services/services/customizing.service";
import {FeedbackPopupComponent} from "./feedback-popup/feedback-popup.component";
import {AnalyticsService} from "src/app/core/services/services/analytics/analytics.service";
import {DragDropModule, CdkDrag, CdkDragHandle} from "@angular/cdk/drag-drop";
import {MatIcon} from "@angular/material/icon";
import {MatButtonToggleGroup, MatButtonToggle} from "@angular/material/button-toggle";
import {NgClass} from "@angular/common";
import {MatFormField} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {ReactiveFormsModule, FormsModule} from "@angular/forms";
import {FileDragDropComponent} from "../../file-input/components/file-drag-drop/file-drag-drop.component";
import {EscapeHotkeyDirective} from "../../hotkey/directives/escape-hotkey.directive";

export interface IFeedbackSubjects {
	name: string;
	selected: boolean;
}

export interface ITicket {
	summary: string;
	description: string;
	userEmail: string;
	userName: string;
	system: string;
}

export interface IFeedbackDialogData {
	status?: number;
	text?: string;
}

@Component({
	selector: "app-bug-report-dialog",
	templateUrl: "./bug-report-dialog.component.html",
	styleUrls: ["./bug-report-dialog.component.scss"],
	imports: [
		CdkDrag,
		MatIcon,
		CdkDragHandle,
		MatButtonToggleGroup,
		MatButtonToggle,
		NgClass,
		MatFormField,
		MatInput,
		ReactiveFormsModule,
		FormsModule,
		FileDragDropComponent,
		EscapeHotkeyDirective,
		TranslatePipe,
	],
})
export class BugReportDialogComponent implements OnInit, AfterViewInit {
	@ViewChild(FileDragDropComponent) private fileDragDrop?: FileDragDropComponent;
	readonly maxAttachmentSizeMiB = 20;

	@NgModule({
		imports: [DragDropModule],
	})
	@Output()
	public valueChange = new EventEmitter<string>();

	attachment?: File;
	applicationUser!: ApplicationUser;

	public isSending = false;
	public system!: string;
	public subjectSelected = false;
	public selectedSubject!: string;
	public summary: string = "";
	@ViewChild("feedbackInput") private feedbackInput?: ElementRef<HTMLTextAreaElement>;

	public subjects: IFeedbackSubjects[] = [
		{name: "Bug", selected: false},
		{name: "Feature", selected: false},
		{name: "Usability", selected: false},
		{name: "Other", selected: false},
	];

	constructor(
		public dialogRef: MatDialogRef<BugReportDialogComponent>,
		public ticketService: TicketService,
		private messageService: MessageService,
		protected translate: TranslateService,
		public loginService: AuthenticationService,
		private customizingService: CustomizingService,
		private _translate: TranslateService,
		public dialog: MatDialog,
		private analyticsService: AnalyticsService,
	) {}

	private readonly data = inject<IFeedbackDialogData | undefined>(MAT_DIALOG_DATA, {optional: true});

	ngOnInit(): void {
		if (this.data?.text) {
			this.changeSubject("Bug");
			this.summary = this.data.text;
		}
		this.getEnvironment();
		this.loginService.getApplicationUser().then((applicationUser) => (this.applicationUser = applicationUser));
	}

	ngAfterViewInit(): void {
		if (this.data?.text) {
			setTimeout(() => {
				const input = this.feedbackInput?.nativeElement;
				input?.focus();
				input?.setSelectionRange(input.value.length, input.value.length);
			});
		}
	}

	async sendFeedback() {
		this.isSending = true;
		const employee = this.applicationUser?.employee;

		const ticket: ITicket = {
			summary: this.summary,
			description: this.selectedSubject,
			userEmail: employee?.email ?? "",
			userName: [employee?.firstName, employee?.lastName].filter(Boolean).join(" "),
			system: this.system,
		};

		// follows Jira Formating
		// https://jira.atlassian.com/secure/WikiRendererHelpAction.jspa?section=all
		ticket.summary += `

    ----
    h3. *System Information:*
    ${this.analyticsService.getSystemInformation()}`;

		const formData = new FormData();

		formData.append("dto", JSON.stringify(ticket));
		if (this.attachment) {
			formData.append("file", this.attachment);
		}

		this.ticketService.sendTicket(formData).subscribe({
			next: (ticketKey) => {
				this.isSending = false;
				if (!ticketKey) {
					this.messageService.add(
						this.translate.instant("mep.components.bug-report.dev-environment-warning"),
						AlertEnum.WARNING,
					);
					this.dialogRef.close(false);
					return;
				}
				this.messageService.add(this.translate.instant("mep.components.bug-report.success"), AlertEnum.SUCCESS);
				this.dialogRef.close(true);
				this.openDialog(ticketKey);
			},
			error: () => {
				this.isSending = false;
				this.messageService.add(this.translate.instant("mep.components.bug-report.error"), AlertEnum.DANGER);
			},
		});
	}

	openDialog(ticket: string) {
		this.dialog.open(FeedbackPopupComponent, {
			disableClose: false,
			panelClass: "mep-dialog",
			data: {
				params: {},
				ticket: ticket,
			},
		});
	}

	onFileChosen(file?: File) {
		if (!file) return;
		if (this.fileSizeOk(file)) {
			this.attachment = file;
		} else {
			this.fileDragDrop?.writeValue(this.attachment);
			const message = this.translate.instant("mep.components.bug-report.attachment-too-large", {
				maxSize: this.maxAttachmentSizeMiB,
			});
			this.messageService.add(message, AlertEnum.WARNING);
		}
	}

	removeAttachment() {
		this.attachment = undefined;
		this.fileDragDrop?.writeValue(undefined);
	}

	private fileSizeOk(file: File) {
		return file.size <= this.maxAttachmentSizeMiB * 1024 * 1024;
	}

	changeSubject(newSubject: string) {
		this.selectedSubject = newSubject;
		this.subjectSelected = true;
		this.subjects.forEach((subject) => (subject.selected = subject.name === newSubject));
	}

	public getEnvironment() {
		const availableEnvironments = [
			"dev",
			"testGBP",
			"testMIA",
			"testMSA",
			"testM3",
			"testZM",
			"intGBP",
			"prodGBP",
			"prodMSA",
			"prodM3",
			"prodMIA",
			"prodGILLARDON",
			"prodSCHWEIZ",
			"prodCONPLAN",
			"prodZT",
			"ci",
			"testGILLARDON",
			"testCOMPLIANCE",
			"testSCHWEIZ",
			"testCONPLAN",
			"testGLOBAL",
			"testGBA",
			"testQA"
		];
		this.customizingService.getEnvironment().then((env: string) => {
			let key: string;
			if (availableEnvironments.includes(env)) {
				key = "mep.environment." + env;
			} else {
				key = "mep.environment.unknown";
			}
			this._translate.get(key).subscribe((name) => {
				this.system = name;
			});
		});
	}
}
